import React from 'react'
import { css } from '@emotion/css'
import { CellRenderer, CellProps } from '../../../legacy-design-system/grid'
import { PureQueryOptions } from 'apollo-client'
import { VulnerabilitySeverity } from '../../../graphql/schema/types/vulnerability'
import { LoadingTextMask } from '../../../legacy-design-system/loading-masks'
import { hasRole, Role } from '../../roles'
import {
  VulnerabilitySeverityBadge,
  VulnerabilitySeverityChangeDropdown,
  Severity
} from '../../vulnerability-severity'
import {
  SinkGroupSeverityName,
  SinkGroupSeverity
} from '../../vulnerability-severity/sink-group-severity'

const dropdownContainerCss = css`
  position: absolute;
  top: calc(50% - 10px);
`

interface GetRendererProps {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

export interface Vulnerability {
  readonly id: string
  readonly severity: VulnerabilitySeverity
  readonly defaultSeverity: VulnerabilitySeverity
  readonly application: {
    readonly role: Role
  }
  readonly visible: boolean
}

export interface SinkGroup {
  readonly severity: SinkGroupSeverityName
}

export interface RowData {
  readonly sinkGroup?: SinkGroup
  readonly vulnerability?: Vulnerability
}

export function getSeverityCellRenderer({
  organizationID,
  refetchQueries,
  refresh
}: GetRendererProps): CellRenderer<RowData> {
  return ({ rowData, getCellContentProps }) => {
    if (rowData.vulnerability) {
      return renderVulnerabilitySeverityCell({
        organizationID,
        refetchQueries,
        refresh,
        rowData: rowData.vulnerability,
        getCellContentProps
      })
    } else if (rowData.sinkGroup) {
      return renderSinkGroupSeverityCell({
        rowData: rowData.sinkGroup,
        getCellContentProps
      })
    }
    throw new Error(
      `Invalid severity row. Row data should contain a vulnerability or sink group: ${rowData}`
    )
  }
}

function renderVulnerabilitySeverityCell({
  organizationID,
  refetchQueries,
  refresh,
  rowData: {
    id,
    severity: severityName,
    defaultSeverity,
    application,
    visible
  },
  getCellContentProps
}: GetRendererProps & CellProps<Vulnerability>) {
  const severity = Severity.fromName(severityName)
  const role = (application && application.role) || 'VIEW'
  return (
    <div {...getCellContentProps({})}>
      {visible && hasRole(role, 'RULES_ADMIN') ? (
        <div className={dropdownContainerCss}>
          <VulnerabilitySeverityChangeDropdown
            organizationID={organizationID}
            vulnerabilityID={id}
            defaultSeverity={Severity.fromName(defaultSeverity)}
            severity={severity}
            refetchQueries={refetchQueries}
            refresh={refresh}
          />
        </div>
      ) : (
        <VulnerabilitySeverityBadge severity={severity} />
      )}
    </div>
  )
}

function renderSinkGroupSeverityCell({
  rowData: { severity: severityName }
}: CellProps<SinkGroup>) {
  const severity = SinkGroupSeverity.fromName(severityName)

  return <VulnerabilitySeverityBadge severity={severity} />
}

export function loadingVulnerabilitySeverityCellRenderer() {
  return <LoadingTextMask />
}
